import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import deepEqual from 'deep-equal';
import { aggregateContent } from './api';
import { Aggregate, Filter, GroupFilterMode } from './types';
import type { ContentType } from '@ilteducation/content-registry-types';

type AggregateState = {
  contentType: string;
  query?: string;
  fields: string[];
  filter: Filter;
  isLoading: boolean;
  aggregation: Aggregate[];
  groupFilterMode: GroupFilterMode;
};

const useAggregation = (contentType: ContentType) =>
  defineStore(`content-aggregation-${contentType}`, () => {
    const aggregateState = ref<AggregateState>();

    const result = computed(() => aggregateState.value?.aggregation ?? []);
    const isLoading = computed(() => aggregateState.value?.isLoading ?? false);

    const aggregate = (
      fields: string[],
      filter: Filter,
      resetState = false,
      size?: number,
      query?: string,
      groupFilterMode: GroupFilterMode = 'or',
    ) => {
      if (fields.length === 0 || fields.some((str) => str === '')) {
        console.error('No fields provided for aggregation or provided empty string');
        return;
      }
      if (isLoading.value) {
        return;
      }

      if (query === aggregateState.value?.query && deepEqual(filter, aggregateState.value?.filter)) {
        return;
      }

      /* Parameter "filter" is passed in as a reference,
    which causes the deepEqual to always be truthy, so solve this,
    we create a deep copy of it */
      const formatedFilter: Filter = JSON.parse(JSON.stringify(filter));

      aggregateState.value = {
        contentType,
        fields,
        filter: formatedFilter,
        isLoading: true,
        aggregation: resetState ? [] : result.value,
        groupFilterMode,
      };

      aggregateContent(contentType, fields, filter, size, query, groupFilterMode)
        .then(({ aggregates }) => {
          aggregateState.value = {
            contentType,
            query,
            fields,
            filter: formatedFilter,
            isLoading: false,
            aggregation: aggregates,
            groupFilterMode,
          };
        })
        .catch(() => {
          aggregateState.value = undefined;
        });
    };

    const clear = () => {
      aggregateState.value = undefined;
    };

    return {
      aggregate,
      isLoading,
      result,
      clear,
    };
  })();

export default useAggregation;
